/**
 * CR107: Magical UX Layer - Encouraging Feedback
 * 
 * Provides delightful "you're doing great" feedback through toast notifications
 */

import { useEffect, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { useMagicalUX } from "./MagicalUXProvider";

const ENCOURAGING_MESSAGES = {
  'header-added': [
    "✨ Nice! Your header is ready.",
    "✨ Perfect! That header looks great.",
    "✨ Excellent choice for your header.",
  ],
  'search-added': [
    "✨ Great! Users can now find what they need.",
    "✨ Smart move adding search functionality.",
    "✨ Perfect! Search will help users browse faster.",
  ],
  'filters-added': [
    "✨ Brilliant! Filters make browsing so much easier.",
    "✨ Great choice — that layout works well for filters.",
    "✨ Nice! Users will love having filter options.",
  ],
  'results-added': [
    "✨ Excellent! Your results grid looks perfect.",
    "✨ Great choice — that layout works well for search results.",
    "✨ Perfect! Users can now browse your content easily.",
  ],
  'layout-updated': [
    "✨ Looking good! Your layout is coming together.",
    "✨ Nice adjustment! That flows much better.",
    "✨ Perfect! You're building something great.",
  ],
  'first-build': [
    "🎉 Amazing! Your page just came to life!",
    "✨ Congratulations! You've built something wonderful.",
    "🌟 Fantastic! Your layout is ready to impress users.",
  ],
};

export function EncouragingFeedback() {
  const { state } = useMagicalUX();

  const showEncouragement = useCallback((actionType: string) => {
    if (!state.isBeginnerMode) return;

    const messages = ENCOURAGING_MESSAGES[actionType as keyof typeof ENCOURAGING_MESSAGES];
    if (!messages) return;

    const message = messages[Math.floor(Math.random() * messages.length)];
    
    toast({
      description: message,
      duration: 3000,
      className: "bg-background border-border text-foreground",
    });
  }, [state.isBeginnerMode]);

  useEffect(() => {
    if (state.lastAction) {
      showEncouragement(state.lastAction);
    }
  }, [state.lastAction, showEncouragement]);

  return null; // This component only manages side effects
}